<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white border-b">
      <div class="mx-auto px-4 py-4">
        <div class="tw-flex items-center justify-between">
          <div class="tw-flex items-center gap-4">
            <ds-button
              variant="ghost"
              size="icon"
              icon="arrow-left"
              @click="goBack"
            />
            <div class="tw-flex items-center gap-2">
              <Icon name="map" class="h-6 w-6 text-primary"/>
              <h1 class="text-2xl font-bold">
                {{ isNew ? 'Create New Map' : (map && map.name) ? map.name : 'Edit Map' }}
              </h1>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mx-auto px-4 py-8">
      <div class="gap-4 tw-flex flex-row ">
        <!-- Configuration Panel -->
        <div class="space-y-4 w-1/3">
          <div class="bg-white rounded-lg shadow-sm border">
            <div class="p-6 border-b">
              <h2 class="text-xl font-semibold">Map Configuration</h2>
              <p class="text-gray-600 mt-1">
                Configure your map settings and spotting areas
              </p>
            </div>
            <div class="p-6 space-y-6">
              <!-- Map Name -->
              <div class="space-y-2">
                <label for="mapName" class="block text-sm font-medium text-gray-700">
                  Map Name <span class="text-red-500">*</span>
                </label>
                <ds-input
                  id="mapName"
                  v-model="mapName"
                  placeholder="Enter map name..."
                  :class="nameError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                  @blur="validateName"
                  is-simplified
                />
                <p v-if="nameError" class="text-red-600 text-sm">{{ nameError }}</p>
              </div>

              <!-- API Key -->
              <div class="space-y-2">
                <label for="apiKey" class="block text-sm font-medium text-gray-700">
                  API Key
                </label>
                <div class="tw-flex gap-2">
                  <ds-input
                    id="apiKey"
                    v-model="apiKey"
                    :readonly="true"
                    class="font-mono text-sm"
                    is-simplified
                  />
                  <ds-button
                    variant="rounded-secondary"
                    size="small"
                    label="Copy"
                    @click="copyApiKey"
                  />
                </div>
              </div>

              <div class="tw-flex items-center gap-3">
                <input
                  id="showAddOrganisationButton"
                  type="checkbox"
                  v-model="mapConfig.showEcosystemLogo"
                  class="h-2 w-2 text-primary focus:ring-primary border-gray-300 rounded mb-2"
                />
                <label for="showAddOrganisationButton" class="text-sm text-gray-700">
                  Show the ecosystem logo and "By ..." text in the top right corner
                </label>
              </div>
              <p class="text-xs text-gray-500 ml-7 !mt-0">
                When enabled, in the top right of the map the ecosystem logo will be displayed.
              </p>

              <!-- Map Center & Zoom -->
              <div class="space-y-4">
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">
                    Map Center & Zoom Level
                  </label>
                  <div class="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <label class="text-xs text-gray-500">Zoom: {{ mapConfig.zoom }}</label>
                    </div>
                    <div>
                      <label class="text-xs text-gray-500">Lat: {{ parseFloat(mapConfig.lat).toFixed(6) }}</label>
                    </div>
                    <div>
                      <label class="text-xs text-gray-500">Lng: {{ parseFloat(mapConfig.lng).toFixed(6) }}</label>
                    </div>
                  </div>
                  <ds-button
                    variant="rounded-secondary"
                    label="Set on Map"
                    icon="map"
                    size="small"
                    @click="openMapModal"
                    class="w-full"
                  />
                  <p class="text-xs text-gray-500">
                    Click to open an interactive map to visually set the center point and zoom level
                  </p>
                </div>

                <!-- Manual input (collapsed by default) -->
                <div class="border-t pt-4">
                  <button
                    type="button"
                    @click="showManualInputs = !showManualInputs"
                    class="tw-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    <Icon :name="showManualInputs ? 'chevron-down' : 'chevron-right'" size="14"/>
                    Manual Input
                  </button>

                  <div v-if="showManualInputs" class="mt-3 space-y-4">
                    <!-- Default Zoom Level -->
                    <div class="space-y-2">
                      <label for="zoom" class="block text-sm font-medium text-gray-700">
                        Default Zoom Level
                      </label>
                      <ds-input
                        id="zoom"
                        v-model="mapConfig.zoom"
                        type="number"
                        :min="1"
                        :max="18"
                        :class="zoomError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                        @blur="validateZoom"
                      />
                      <p v-if="zoomError" class="text-red-600 text-sm">{{ zoomError }}</p>
                    </div>

                    <!-- Map Center Coordinates -->
                    <div class="grid grid-cols-2 gap-4">
                      <div class="space-y-2">
                        <label for="lat" class="block text-sm font-medium text-gray-700">
                          Latitude
                        </label>
                        <ds-input
                          id="lat"
                          v-model="mapConfig.lat"
                          type="number"
                          step="0.000001"
                          :min="-90"
                          :max="90"
                          :class="latError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                          @blur="validateLat"
                        />
                        <p v-if="latError" class="text-red-600 text-sm">{{ latError }}</p>
                      </div>
                      <div class="space-y-2">
                        <label for="lng" class="block text-sm font-medium text-gray-700">
                          Longitude
                        </label>
                        <ds-input
                          id="lng"
                          v-model="mapConfig.lng"
                          type="number"
                          step="0.000001"
                          :min="-180"
                          :max="180"
                          :class="lngError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                          @blur="validateLng"
                        />
                        <p v-if="lngError" class="text-red-600 text-sm">{{ lngError }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Marker Settings -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <label for="markerIcon" class="block text-sm font-medium text-gray-700">
                    Marker Icon
                  </label>
                  <select
                    id="markerIcon"
                    v-model="mapConfig.markerIcon"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="default">Default</option>
                    <option value="pin">Pin</option>
                    <option value="circle">Circle</option>
                    <option value="star">Star</option>
                    <option value="triangle">Triangle</option>
                  </select>
                </div>
                <div class="space-y-2">
                  <label for="markerIcon" class="block text-sm font-medium text-gray-700">
                    Default Marker Color
                  </label>
                  <input class="w-full h-8" type="color" v-model="mapConfig.markerColor" @update:modelValue="updateMarkerColor"/>
                </div>
              </div>

              <!-- Country Preferences -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <label for="preferredCountry" class="block text-sm font-medium text-gray-700">
                    Preferred Country
                    <Icon name="information" size="14"
                          v-tooltip="'When companies have multiple locations, prioritize showing their office in this country on the map instead of their headquarters location.'"/>
                  </label>
                  <dropdown
                    :options="COUNTRIES"
                    search
                    v-model="mapConfig.preferredCountry"
                    is-simplified
                    new-append-icon="chevron-down"
                  />
                </div>
                <div class="space-y-2">
                  <label for="preferredConnectionsCountry" class="block text-sm font-medium text-gray-700">
                    Preferred Connections Country
                    <Icon name="information" size="14"
                          v-tooltip="'Highlight and filter companies that have business connections or relationships with companies from this country.'"/>
                  </label>
                  <dropdown
                    :options="COUNTRIES"
                    search
                    v-model="mapConfig.preferredConnectionsCountry"
                    is-simplified
                    new-append-icon="chevron-down"
                  />
                </div>
              </div>

              <!-- Spotting Area -->
              <div class="space-y-2">
                <label for="spottingArea" class="block text-sm font-medium text-gray-700">
                  Spotting Area
                </label>
                <dropdown
                  id="spottingArea"
                  is-simplified
                  new-append-icon="chevron-down"
                  :options="availableSpottingAreas"
                  v-model="selectedSpottingAreaId"
                />

                <!-- Allowed facet filters -->
              </div>

              <div class="space-y-2">
                <Checkbox :model-value="mapConfig.showContent" v-model="mapConfig.showContent"
                          label="Do you want to enable content on the map? This will add an extra tab"/>
              </div>

              <div class="space-y-2" v-if="mapConfig.showContent">
                <label for="spottingArea" class="block text-sm font-medium text-gray-700">
                  What would you call the tab where spotting area content is shown?
                </label>
                <dropdown :options="contentLabelOptions" v-model="mapConfig.contentLabel" is-simplified/>
              </div>

              <!-- Visible Report Fields -->
              <div class="space-y-2 !mt-6">
                <label class="block text-sm font-medium text-gray-700 mt-4">
                  Report Field Filters
                  <Icon name="information" size="14"
                        v-tooltip="'Select reports and their fields to filter what companies appear on the map. Only companies with selected values for these fields will be visible. Only multi select fields are configurable'"/>
                </label>

                <!-- Report Selectors -->
                <div class="space-y-4">
                  <div
                    v-for="(reportConfig, index) in visibleReportFilters"
                    :key="index"
                    class="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div class="tw-flex items-center justify-between mb-4">
                      <h4 class="text-sm font-medium text-gray-700">Report Filter {{ index + 1 }}</h4>
                      <ds-button
                        v-if="index > 0"
                        variant="ghost"
                        size="small"
                        icon="x"
                        @click="removeReportFilter(index)"
                        class="text-red-600 hover:text-red-700"
                      />
                    </div>

                    <div class="space-y-4">
                      <!-- Report Template Selection -->
                      <div>
                        <label class="block text-xs font-medium text-gray-600 mb-1">Report Template</label>
                        <div class="tw-flex items-center gap-2">
                          <div class="flex-1">
                            <dropdown
                              :options="availableReportTemplates"
                              search
                              v-model="reportConfig.reportTemplateId"
                              @update:modelValue="onReportTemplateChange(index, $event)"
                              placeholder="Select a report template..."
                              is-simplified
                              new-append-icon="chevron-down"
                            />
                          </div>
                          <ds-button
                            v-if="reportConfig.reportTemplateId"
                            variant="ghost"
                            size="small"
                            icon="x"
                            @click="clearReportTemplate(index)"
                            class="text-gray-500 hover:text-gray-700"
                            title="Clear selection"
                          />
                        </div>
                      </div>

                      <!-- Fields from selected report -->
                      <div v-if="reportConfig.reportTemplateId" class="space-y-3">
                        <div class="border-t pt-3">
                          <label class="block text-xs font-medium text-gray-600 mb-2">Fields from this report</label>

                          <div
                            v-for="(fieldConfig, fieldIndex) in reportConfig.fields"
                            :key="fieldIndex"
                            class="border border-gray-100 rounded p-3 bg-white"
                          >
                            <div class="tw-flex items-center justify-between mb-2">
                              <div class="flex-1">
                                <label class="block text-xs font-medium text-gray-600 mb-1">Field</label>
                                <dropdown
                                  :options="getAvailableFieldsForTemplateExcludingSelected(reportConfig.reportTemplateId, index, fieldIndex).map(f => ({ label: f.label, value: f.id }))"
                                  search
                                  v-model="fieldConfig.fieldId"
                                  @update:modelValue="onFieldChange(index, fieldIndex, $event)"
                                  placeholder="Select a field..."
                                  is-simplified
                                  new-append-icon="chevron-down"
                                />
                              </div>
                              <ds-button
                                v-if="reportConfig.fields.length > 1"
                                variant="ghost"
                                size="small"
                                icon="x"
                                @click="removeField(index, fieldIndex)"
                                class="text-red-500 hover:text-red-700 ml-2"
                              />
                            </div>

                            <!-- Value Selection for this field -->
                            <div>
                              <label class="block text-xs text-gray-500 mb-1">Allowed Values with Colors (Note: only multi select fields are configurable)</label>
                              <div class="space-y-1">
                                <div
                                  v-for="option in getFieldOptions(fieldConfig.fieldId)"
                                  :key="option.value"
                                  class="tw-flex items-center gap-2 border border-gray-300 bg p-1 rounded bg-gray-50"
                                >
                                  <input
                                    :id="`report_${index}_field_${fieldIndex}_${option.value}`"
                                    type="checkbox"
                                    :value="option.value"
                                    v-model="fieldConfig.allowedValues"
                                    @change="onFieldValueToggle(index, fieldIndex, option.value)"
                                    class="h-3 w-3 mb-2 text-primary focus:ring-primary border-gray-300 rounded"
                                  />
                                  <label
                                    :for="`report_${index}_field_${fieldIndex}_${option.value}`"
                                    class="text-xs text-gray-700 flex-1"
                                  >
                                    {{ option.label }}
                                  </label>
                                  <input
                                    v-if="fieldConfig.allowedValues.includes(option.value)"
                                    type="color"
                                    v-model="fieldConfig.colorAssignments[option.value]"
                                    @input="onFieldValueColorChange(index, fieldIndex, option.value)"
                                    class="w-6 h-6 rounded border border-gray-300 cursor-pointer"
                                    :title="`Color for ${option.label}`"
                                  />
                                </div>
                              </div>

                              <div class="mt-1">
                                <ds-button
                                  variant="rounded-secondary"
                                  size="small"
                                  :label="fieldConfig.allowedValues.length === getFieldOptions(fieldConfig.fieldId).length ? 'Deselect All' : 'Select All'"
                                  @click="toggleAllFieldValues(index, fieldIndex)"
                                  class="text-xs"
                                />
                              </div>
                            </div>
                          </div>

                          <!-- Add Field Button -->
                          <ds-button
                            variant="rounded-secondary"
                            size="small"
                            icon="plus"
                            label="Add Field"
                            @click="addFieldToReport(index)"
                            class="text-blue-600 hover:text-blue-700 mt-2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Add Report Filter Button -->
                  <ds-button
                    variant="rounded-secondary"
                    size="small"
                    icon="plus"
                    label="Add Report Filter"
                    @click="addReportFilter"
                    class="text-blue-600 hover:text-blue-700"
                  />

                  <p class="text-xs text-gray-500">
                    Only companies that have one of the selected values for ALL configured fields will appear on the map.
                  </p>
                </div>
              </div>

              <!-- Showcase Category -->
              <!--              <div class="space-y-2">
                              <label class="block text-sm font-medium text-gray-700">
                                Showcase Category
                                <Icon name="information" size="14"
                                      v-tooltip="'Select a knowledge base category to showcase posts as an additional tab on the map. Posts from this category will be visible to map users.'"/>
                              </label>
                              <dropdown
                                :options="availableKnowledgeBaseCategories"
                                search
                                v-model="selectedKnowledgeBaseCategoryId"
                                placeholder="Select a category..."
                                is-simplified
                                new-append-icon="chevron-down"
                                clearable
                              />
                              <p class="text-xs text-gray-500">
                                Users can configure individual posts to show or hide on the map from the post editor.
                              </p>
                            </div>-->

              <!-- Allowed Domains -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  Allowed Domains
                  <Icon class="ml-1" name="information" size="14"
                        v-tooltip="'Enter the domains where this map will be embedded (e.g., example.com or subdomain.example.com). This is required for security and will be used to set CORS headers.'"/>
                </label>

                <!-- Domain Inputs -->
                <div class="space-y-2">
                  <div
                    v-for="(domain, index) in allowedDomains"
                    :key="index"
                    class="tw-flex items-center gap-2"
                  >
                    <ds-input
                      v-model="allowedDomains[index]"
                      :placeholder="index === 0 ? 'example.com' : 'additional-domain.com'"
                      :class="getDomainError(index) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                      @blur="validateDomains"
                      is-simplified
                      class="flex-1"
                    />
                    <ds-button
                      v-if="index > 0"
                      variant="ghost"
                      size="small"
                      icon="x"
                      @click="removeDomain(index)"
                      class="text-red-600 hover:text-red-700"
                    />
                  </div>

                  <!-- Add Domain Button -->
                  <ds-button
                    variant="rounded-secondary"
                    size="small"
                    icon="plus"
                    label="Add Domain"
                    @click="addDomain"
                    class="text-blue-600 hover:text-blue-700"
                  />
                </div>

                <div v-if="domainsError" class="text-red-600 text-sm">{{ domainsError }}</div>
                <p class="text-xs text-gray-500">
                  Only these domains will be allowed to embed your map. Don't include http:// or https://
                </p>
              </div>

              <!-- User Interface Options -->
              <div class="space-y-4">
                <label class="block text-sm font-medium text-gray-700">
                  User Interface Options
                </label>

                <!-- Add Organisation Button -->
                <div class="tw-flex items-center gap-3">
                  <input
                    id="showAddOrganisationButton"
                    type="checkbox"
                    v-model="mapConfig.showAddOrganisationButton"
                    class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mb-2"
                  />
                  <label for="showAddOrganisationButton" class="text-sm text-gray-700">
                    Show "Add your organisation" button
                  </label>
                </div>
                <p class="text-xs text-gray-500 ml-7 !mt-0">
                  When enabled, users can request to add their organisation to the map
                </p>

                <!-- Simplified Actor Filters -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">
                    Visible Filter Controls
                    <Icon name="information" size="14" class="ml-1"
                          v-tooltip="'Configure which filter controls are visible on the simplified actor view of the map'"/>
                  </label>
                  <dropdown
                    :options="availableSimplifiedFilterOptions"
                    multiple
                    v-model="mapConfig.simplifiedFilterControls"
                    placeholder="Select visible filters..."
                    is-simplified
                    new-append-icon="chevron-down"
                    class="w-full"
                    search
                  />
                  <p class="text-xs text-gray-500">
                    Choose which filter controls users can see when browsing organisations on the map
                  </p>
                </div>
              </div>

              <!-- Map Status -->
              <div class="space-y-2">
                <label for="mapStatus" class="block text-sm font-medium text-gray-700">
                  Map Status
                </label>
                <div class="tw-flex items-center gap-4">
                  <div class="tw-flex items-center gap-2">
                    <input
                      id="statusActive"
                      type="radio"
                      value="active"
                      v-model="mapConfig.status"
                      class="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <label for="statusActive" class="text-sm text-gray-700 mt-2">Active</label>
                  </div>
                  <div class="tw-flex items-center gap-2">
                    <input
                      id="statusInactive"
                      type="radio"
                      value="inactive"
                      v-model="mapConfig.status"
                      class="h-4 w-4 text-gray-400 focus:ring-gray-400 border-gray-300"
                    />
                    <label for="statusInactive" class="text-sm text-gray-700 mt-2">Inactive</label>
                  </div>
                  <div class="tw-flex items-center gap-2">
                    <input
                      id="statusDraft"
                      type="radio"
                      value="draft"
                      v-model="mapConfig.status"
                      class="h-4 w-4 text-yellow-500 focus:ring-yellow-400 border-gray-300"
                    />
                    <label for="statusDraft" class="text-sm text-gray-700 mt-2">Draft</label>
                  </div>
                </div>
                <p class="text-xs text-gray-500">
                  Only active maps will be accessible via the embed URL
                </p>
              </div>

              <!-- Save Button -->
              <div class="pt-4 border-t">
                <ds-button
                  class="w-full"
                  :label="saving ? 'Saving...' : 'Save Changes'"
                  @click="handleSave"
                  variant="rounded"
                  :disabled="saving || !isFormValid"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Preview Panel -->
        <div class="w-2/3 sticky top-8 h-fit max-h-[calc(100vh-120px)] overflow-y-auto" :key="livePreviewKey"
        >
          <div class="space-y-4">
            <div class="bg-white rounded-lg shadow-sm border">
              <div class="p-6 border-b">
                <h2 class="text-xl font-semibold tw-flex items-center gap-2">
                  <Icon name="eye" class="h-5 w-5"/>
                  Live Preview
                  <span v-if="hasUnsavedChanges" class="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">Unsaved changes</span>
                </h2>
                <p class="text-gray-600 mt-1">
                  See how your map will appear when embedded
                </p>
              </div>
              <div class="p-6">
                <!-- Map Preview -->
                <div class="bg-gray-100 rounded-lg border overflow-hidden">
                  <!-- Live embed iframe with current form values -->
                  <div
                    v-if="livePreviewEmbedUrl"
                    ref="mapContainer"
                    class="w-full h-[600px] bg-gray-200 rounded-lg relative overflow-hidden"
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      :src="livePreviewEmbedUrl"
                      frameborder="0"
                      allowfullscreen
                      sandbox="allow-scripts allow-same-origin"
                      @error="handleIframeError"
                      @load="handleIframeLoad"
                    ></iframe>
                    <div v-if="iframeLoading" class="absolute inset-0 bg-white bg-opacity-75 tw-flex items-center justify-center">
                      <div class="text-center">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                        <div class="text-sm text-gray-600">Loading map preview...</div>
                      </div>
                    </div>
                    <div v-if="iframeError" class="absolute inset-0 bg-red-50 tw-flex items-center justify-center border-2 border-red-200">
                      <div class="text-center text-red-700 p-4">
                        <div class="text-lg mb-2">🔒 {{ corsError ? 'CORS Error' : 'SSL Certificate Error' }}</div>
                        <div class="text-sm mb-4">
                          {{ corsError ? 'The embed endpoint has CORS restrictions for local development' : 'The embed URL has SSL certificate issues for local development' }}
                        </div>
                        <div class="space-y-2">
                          <ds-button
                            variant="outline"
                            size="small"
                            label="Retry"
                            @click="retryEmbed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Fallback placeholder -->
                  <div v-else class="bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 h-96 tw-flex items-center justify-center">
                    <div class="text-center">
                      <Icon name="map" class="w-12 h-12 text-gray-400 mx-auto mb-3"/>
                      <h3 class="text-lg font-medium text-gray-900 mb-2">
                        Map Preview
                      </h3>
                      <p class="text-gray-500 mb-4">
                        {{ isNew ? 'Save your map first to see the live preview' : 'Configure your spotting area to see the preview' }}
                      </p>
                      <div v-if="selectedSpottingAreaId && isNew" class="text-xs text-gray-500">
                        Spotting area selected. Save to generate preview.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Embed Code -->
            <div class="bg-white rounded-lg shadow-sm border">
              <div class="p-6 border-b">
                <h2 class="text-xl font-semibold tw-flex items-center gap-2">
                  <Icon name="code" class="h-5 w-5"/>
                  Embed Code

                  {{ $store.getters.url }}
                </h2>
                <p class="text-gray-600 mt-1">
                  Copy this code to embed your map
                </p>
              </div>
              <div class="p-6" v-if="map && map.embedUrl">
                <h2 class="text-xl font-semibold tw-flex items-center gap-2">
                  <a target="_blank" :href="map.embedUrl">Preview the map here</a>
                </h2>
              </div>
              <div class="p-6 space-y-6">
                <!-- Option 1: Simple iframe -->
                <div class="embed-option">
                  <div class="embed-option-header">
                    <h4 class="embed-option-title">Option 1: Simple Iframe</h4>
                    <span class="embed-option-badge">Easiest</span>
                  </div>
                  <p class="embed-option-description">
                    Just copy and paste this code into your website. No technical knowledge required.
                  </p>
                  <div class="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <code class="text-green-400 text-sm whitespace-pre">{{ embedCode || 'Save your map to generate embed code' }}</code>
                  </div>
                  <ds-button
                    class="mt-3 w-full"
                    variant="outline"
                    label="Copy Iframe Code"
                    @click="copyEmbedCode"
                    :disabled="!embedCode"
                  />
                </div>

                <!-- Option 2: JavaScript Widget -->
                <div class="embed-option">
                  <div class="embed-option-header">
                    <h4 class="embed-option-title">Option 2: JavaScript Widget</h4>
                    <span class="embed-option-badge advanced">Advanced</span>
                  </div>
                  <p class="embed-option-description">
                    Requires basic HTML knowledge.
                  </p>
                  <div class="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <code class="text-green-400 text-sm whitespace-pre">{{ embedCodeScript || 'Save your map to generate embed code' }}</code>
                  </div>
                  <ds-button
                    class="mt-3 w-full"
                    variant="outline"
                    label="Copy JavaScript Code"
                    @click="copyEmbedCodeScript"
                    :disabled="!embedCodeScript"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Map Selection Modal -->
    <div v-if="showMapModal" class="fixed inset-0 bg-black bg-opacity-50 tw-flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl w-[90vw] h-[80vh] max-w-6xl relative">
        <!-- Modal Header -->
        <div class="p-6 border-b tw-flex items-center justify-between">
          <h3 class="text-xl font-semibold">Set Map Center & Zoom</h3>
          <button @click="closeMapModal" class="text-gray-400 hover:text-gray-600">
            <Icon name="x" size="24"/>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="p-6 h-[calc(100%-140px)]">
          <div class="tw-flex gap-6 h-full">
            <!-- Map Container -->
            <div class="flex-1">
              <div id="map-selector" class="w-full h-full bg-gray-200 rounded-lg relative">
                <!-- Leaflet map will be mounted here -->
              </div>
            </div>

            <!-- Controls Panel -->
            <div class="w-80 space-y-4">
              <div>
                <h4 class="font-medium mb-2">Instructions</h4>
                <p class="text-sm text-gray-600 mb-4">
                  Pan and zoom to set your desired map view. Click anywhere on the map to set the center point.
                </p>
              </div>

              <div class="space-y-3">
                <div>
                  <label class="text-sm font-medium text-gray-700">Current Zoom Level</label>
                  <div class="text-lg font-mono">{{ tempMapConfig.zoom }}</div>
                </div>
                <div>
                  <label class="text-sm font-medium text-gray-700">Center Latitude</label>
                  <div class="text-sm font-mono">{{ parseFloat(tempMapConfig.lat).toFixed(6) }}</div>
                </div>
                <div>
                  <label class="text-sm font-medium text-gray-700">Center Longitude</label>
                  <div class="text-sm font-mono">{{ parseFloat(tempMapConfig.lng).toFixed(6) }}</div>
                </div>
              </div>

              <div class="pt-4 border-t">
                <ds-button
                  class="w-full mb-3"
                  label="Apply Settings"
                  @click="applyMapSettings"
                />
                <ds-button
                  class="w-full"
                  variant="outline"
                  label="Cancel"
                  @click="closeMapModal"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import * as EcosystemMapsApi from '@/Domain/EcosystemMap/Api/ecosystemMaps'
  import SpottingAreaMixin from '@/util/SpottingAreaMixin'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import { COUNTRIES } from '@/constants/countries'
  import { EcosystemReports } from '../../api/reporting'
  import ConfigMixin from '@/util/ConfigMixin'
  import Checkbox from '@/components/Form/Checkbox.vue'
  import ColorThemeSelector from '@/components/ColorThemeSelector.vue'
  import debounce from 'lodash/debounce'

  export default defineComponent({
    name: 'EcosystemMapDetail',
    mixins: [SpottingAreaMixin, ConfigMixin],
    components: {
      Checkbox,
      ColorThemeSelector,
      Dropdown,
      Icon,
      DsButton,
      DsInput
    },
    data () {
      return {
        saving: false,
        loading: true,
        mapName: '',
        apiKey: '',
        mapConfig: {
          zoom: 4,
          lat: 50.0,
          lng: 15.0,
          markerIcon: 'default',
          markerColor: '#3B82F6',
          preferredCountry: null,
          preferredConnectionsCountry: null,
          status: 'draft',
          showAddOrganisationButton: true,
          simplifiedFilterControls: [],
          contentLabel: 'landscapes',
          showContent: false,
        },
        selectedSpottingAreaId: '',
        allowedDomains: [''],
        visibleReportFilters: [{
          reportTemplateId: null,
          fields: [{ fieldId: null, allowedValues: [], colorAssignments: {} }]
        }],
        reportTemplates: [],
        reportFields: [], // All fields indexed by template ID
        reportFieldOptions: {},
        knowledgeBaseCategories: [],
        map: null,
        error: null,
        validationErrors: {},
        touched: {},
        showManualInputs: false,
        showMapModal: false,
        tempMapConfig: {
          zoom: 4,
          lat: 50.0,
          lng: 15.0
        },
        leafletMap: null,
        iframeError: false,
        corsError: false,
        iframeLoading: false,
        livePreviewKey: 0, // Force iframe refresh when config changes
        refreshDebounceTimer: null
      }
    },
    computed: {
      contentLabelOptions () {
        // [{value: 'landscapes', label: 'Landscapes'},'Showcases','Use Cases', 'News','Reports']
        return [
          {
            value: 'landscapes',
            label: 'Landscapes',
          },
          {
            value: 'showcases',
            label: 'Showcases',
          },
          {
            value: 'use_cases',
            label: 'Use cases',
          },
          {
            value: 'news',
            label: 'News',
          },
          {
            value: 'reports',
            label: 'Reports',
          },
        ]
      },
      COUNTRIES () {
        return Object.keys(COUNTRIES).map(countryCode => ({
          label: COUNTRIES[countryCode],
          value: countryCode
        }))
      },
      availableSpottingAreas () {
        return this.spottingAreas.map(spottingArea => {
          return {
            label: spottingArea.name,
            value: spottingArea.id,
          }
        })
      },
      availableReportTemplates () {
        return this.reportTemplates.map(template => ({
          label: template.name,
          value: template.id
        }))
      },
      availableKnowledgeBaseCategories () {
        return this.knowledgeBaseCategories.map(category => ({
          label: category.name,
          value: category.id
        }))
      },
      availableSimplifiedFilterOptions () {
        var eligibleFilterControlOptions = [...this.filterControlOptionsActor]

        if (this.viewReportOfActorTypes && this.viewReportOfActorTypes.length > 0) {
          eligibleFilterControlOptions.push({ value: 'reports', label: 'Reports' })
        }

        return eligibleFilterControlOptions.sort((a, b) => {
          return a.label.localeCompare(b.label)
        })
      },
      visibleFilterControlOptionsActor () {
        if (!this.publisher || !this.publisher.filterControls) {
          return [{ value: 'sustainability_goal', label: 'Sustainability Goals' }]
        }

        let defaultFilters = [
          { value: 'sustainability_goal', label: 'Sustainability Goals' }
        ]

        if (this.publisher.filterControls.includes('reports')) {
          defaultFilters.push({ value: 'reports', label: 'Reports' })
        }

        return [
          ...defaultFilters,
          ...this.publisher.filterControls.map((control) => {
            return this.translatedFilterControlOptions.find(filterOption => {
              return filterOption.value === control
            })
          })
            .filter((control) => {
              if (!control) {
                return
              }

              // Only allow choosing options that are available on the simplified actors page
              return control.value !== 'sustainability_goal' && this.translatedSimplifiedFilterControlOptions.find(filterOption => {
                return filterOption.value === control.value
              })
            })]
      },
      isNew () {
        return this.$route.params.id === 'new'
      },
      mapId () {
        return this.$route.params.id || 'new'
      },
      mapUrl () {
        return this.map.embedUrl
      },
      embedCode () {
        if (this.map) {
          // For local development, use current domain if embed URL seems invalid
          let embedUrl = this.map.embedUrl

          // Check if we're in local development and fix the URL
          if (embedUrl && (embedUrl.includes('localhost') || embedUrl.includes('127.0.0.1') || !embedUrl.startsWith('http'))) {
            const currentHost = window.location.origin
            const urlParts = embedUrl.split('/ecosystem-maps/')
            if (urlParts.length > 1) {
              embedUrl = `${currentHost}/ecosystem-maps/${urlParts[1]}`
            }
          }

          return `<iframe width="100%" height="100%" src="${embedUrl}" frameborder="0" allowfullscreen></iframe>`
        }

        return ``
      },
      embedCodeScript () {
        if (this.map) {
          return `<div id="ds-interactive-map" data-api-key="${this.map.apiKey}"></div>
<script type="module" src="https://static.datascouts.eu/datascouts-visual-map-platform-connection.umd.js"><\/script>`
        }
        return ``
      },
      previewEmbedUrl () {
        if (this.map) {
          let embedUrl = this.map.embedUrl

          // For local development, always use local domain to avoid SSL issues
          if (this.isLocalDevelopment && embedUrl) {
            const currentHost = window.location.origin
            const urlParts = embedUrl.split('/ecosystem-maps/')
            if (urlParts.length > 1) {
              // Force local URL to avoid SSL certificate issues
              embedUrl = `${currentHost}/ecosystem-maps/${urlParts[1]}`
            }
          }

          return embedUrl
        }

        return null
      },
      isFormValid () {
        const noValidationErrors = Object.keys(this.validationErrors).length === 0
        const hasMapName = this.mapName.trim() !== ''

        // Recalculate hasChanges directly instead of using computed property to avoid timing issues
        const hasReportFiltersContent = this.visibleReportFilters.some(filter => filter.reportTemplateId !== null)

        let hasChanges = false
        if (!this.map) {
          hasChanges = this.mapName.trim() !== '' || this.selectedSpottingAreaId !== '' || hasReportFiltersContent
        } else {
          const mapDomains = this.map.allowedDomains || []
          const domainsChanged = JSON.stringify(this.allowedDomains.filter(d => d.trim() !== '')) !==
            JSON.stringify(mapDomains)

          const mapReportFilters = this.map.reportFieldFilters || []
          const reportFiltersChanged = JSON.stringify(this.visibleReportFilters.filter(f => f.reportTemplateId !== null)) !==
            JSON.stringify(mapReportFilters)

          hasChanges = (
            this.mapName !== this.map.name ||
            this.mapConfig.zoom !== this.map.zoom ||
            this.mapConfig.lat !== this.map.lat ||
            this.mapConfig.lng !== this.map.lng ||
            this.mapConfig.markerIcon !== this.map.markerIcon ||
            this.mapConfig.markerColor !== this.map.markerColor ||
            this.mapConfig.defaultMarkerColor !== this.map.defaultMarkerColor ||
            this.mapConfig.preferredCountry !== this.map.preferredCountry ||
            this.mapConfig.preferredConnectionsCountry !== this.map.preferredConnectionsCountry ||
            this.selectedSpottingAreaId !== this.map.spottingAreaId ||
            this.mapConfig.status !== this.map.status ||
            this.mapConfig.contentLabel !== this.map.contentLabel ||
            this.mapConfig.showEcosystemLogo !== this.map.showEcosystemLogo ||
            this.mapConfig.showAddOrganisationButton !== this.map.showAddOrganisationButton ||
            this.mapConfig.showContent !== this.map.showContent ||
            JSON.stringify(this.mapConfig.simplifiedFilterControls || []) !== JSON.stringify(this.map.simplifiedFilterControls || []) ||
            domainsChanged ||
            reportFiltersChanged
          )
        }

        return noValidationErrors &&
          hasMapName &&
          hasChanges
      },
      nameError () {
        return this.touched.name && this.validationErrors.name
      },
      zoomError () {
        return this.touched.zoom && this.validationErrors.zoom
      },
      latError () {
        return this.touched.lat && this.validationErrors.lat
      },
      lngError () {
        return this.touched.lng && this.validationErrors.lng
      },
      domainsError () {
        return this.touched.domains && this.validationErrors.domains
      },

      isLocalDevelopment () {
        return typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname.includes('.local'))
      },

      currentOrigin () {
        return typeof window !== 'undefined' ? window.location.origin : ''
      },

      hasUnsavedChanges () {
        const hasDomainsContent = this.allowedDomains.some(domain => domain.trim() !== '')
        const hasReportFiltersContent = this.visibleReportFilters.some(filter => filter.reportTemplateId !== null)
        if (!this.map) return this.mapName.trim() !== '' || this.selectedSpottingAreaId !== '' || hasDomainsContent || hasReportFiltersContent

        const mapDomains = this.map.allowedDomains || []
        const domainsChanged = JSON.stringify(this.allowedDomains.filter(d => d.trim() !== '')) !==
          JSON.stringify(mapDomains)

        const mapReportFilters = this.map.reportFieldFilters || []
        const currentFilters = this.visibleReportFilters.filter(f => f.reportTemplateId !== null)
        const reportFiltersChanged = JSON.stringify(currentFilters) !== JSON.stringify(mapReportFilters)

        return (
          this.mapName !== this.map.name ||
          this.mapConfig.zoom !== this.map.zoom ||
          this.mapConfig.lat !== this.map.lat ||
          this.mapConfig.lng !== this.map.lng ||
          this.mapConfig.markerIcon !== this.map.markerIcon ||
          this.mapConfig.markerColor !== this.map.markerColor ||
          this.mapConfig.defaultMarkerColor !== this.map.defaultMarkerColor ||
          this.mapConfig.preferredCountry !== this.map.preferredCountry ||
          this.mapConfig.preferredConnectionsCountry !== this.map.preferredConnectionsCountry ||
          this.selectedSpottingAreaId !== this.map.spottingAreaId ||
          this.mapConfig.status !== this.map.status ||
          this.mapConfig.showAddOrganisationButton !== this.map.showAddOrganisationButton ||
          JSON.stringify(this.mapConfig.simplifiedFilterControls || []) !== JSON.stringify(this.map.simplifiedFilterControls || []) ||
          domainsChanged ||
          reportFiltersChanged
        )
      },

      livePreviewEmbedUrl () {
        // For new maps, don't show preview until saved (no API key available)
        if (this.isNew || !this.map) return null

        // Use apiKey from form data (this.apiKey) as fallback
        const mapApiKey = this.map.apiKey || this.apiKey
        if (!mapApiKey) return null

        // Use the existing API key but with current form configuration as query parameters
        const baseUrl = this.previewEmbedUrl || this.map.embedUrl
        if (!baseUrl) return null

        // Parse the base URL and add current config as query params
        const url = new URL(baseUrl, window.location.origin)

        // Add current form values as override parameters - these have PRIORITY over saved config
        url.searchParams.set('zoom', this.mapConfig.zoom)
        url.searchParams.set('lat', this.mapConfig.lat)
        url.searchParams.set('lng', this.mapConfig.lng)
        url.searchParams.set('markerIcon', this.mapConfig.markerIcon)
        url.searchParams.set('markerColor', this.mapConfig.markerColor)
        url.searchParams.set('status', this.mapConfig.status)
        url.searchParams.set('showEcosystemLogo', this.mapConfig.showEcosystemLogo)

        // Add map name if provided
        if (this.mapName && this.mapName.trim()) {
          url.searchParams.set('name', this.mapName.trim())
        }

        if (this.mapConfig.preferredCountry) {
          url.searchParams.set('preferredCountry', this.mapConfig.preferredCountry)
        }
        if (this.mapConfig.preferredConnectionsCountry) {
          url.searchParams.set('preferredConnectionsCountry', this.mapConfig.preferredConnectionsCountry)
        }
        if (this.selectedSpottingAreaId) {
          url.searchParams.set('spottingAreaId', this.selectedSpottingAreaId)
        }
        if (this.mapConfig.contentLabel) {
          url.searchParams.set('contentLabel', this.mapConfig.contentLabel)
        }
        if (this.mapConfig.showAddOrganisationButton !== undefined) {
          url.searchParams.set('hideAddOrganisationButton', (!this.mapConfig.showAddOrganisationButton).toString())
        }
        if (this.mapConfig.simplifiedFilterControls?.length) {
          url.searchParams.set('simplifiedFilterControls', JSON.stringify(this.mapConfig.simplifiedFilterControls))
        }

        // Pass color assignments for live preview
        if (this.mapConfig.defaultMarkerColor) {
          url.searchParams.set('defaultMarkerColor', this.mapConfig.defaultMarkerColor)
        }

        // Pass report field filters with colors
        const filtersWithColors = this.reportFieldFiltersWithColors
        if (filtersWithColors.reportFieldFilters && filtersWithColors.reportFieldFilters.length > 0) {
          url.searchParams.set('reportFieldFilters', JSON.stringify(filtersWithColors.reportFieldFilters))
        }

        // Add a timestamp to force refresh when config changes
        url.searchParams.set('_t', Date.now())

        return url.toString()
      },

      enabledReportFieldsCount () {
        // Count the total number of enabled report field values across all filters
        let count = 0
        this.visibleReportFilters.forEach(filter => {
          if (filter.reportTemplateId) {
            filter.fields.forEach(field => {
              if (field.fieldId && field.allowedValues && field.allowedValues.length > 0) {
                count += field.allowedValues.length
              }
            })
          }
        })
        return count
      },

      enabledReportFieldValues () {
        // Get detailed information about enabled field values for color assignment
        const fieldValues = []
        this.visibleReportFilters.forEach(filter => {
          if (filter.reportTemplateId) {
            const reportName = this.reportTemplates.find(t => t.id === filter.reportTemplateId)?.name || 'Unknown Report'
            filter.fields.forEach(field => {
              if (field.fieldId && field.allowedValues && field.allowedValues.length > 0) {
                const fieldLabel = this.getFieldLabel(field.fieldId)
                field.allowedValues.forEach(allowedValue => {
                  // Find the display label for this value
                  const fieldOptions = this.getFieldOptions(field.fieldId)
                  const option = fieldOptions.find(opt => opt.value === allowedValue)
                  const valueLabel = option ? option.label : allowedValue

                  fieldValues.push({
                    reportName,
                    fieldLabel,
                    value: allowedValue,
                    valueLabel,
                    displayName: `${fieldLabel}: ${valueLabel}`
                  })
                })
              }
            })
          }
        })
        return fieldValues
      },

      reportFieldFiltersWithColors () {
        // Use the custom colors assigned per field value
        const defaultColor = this.mapConfig.defaultMarkerColor || '#3B82F6'

        // Create report field filters with color assignments
        const filtersWithColors = this.visibleReportFilters
          .filter(filter => filter.reportTemplateId !== null)
          .map(filter => {
            const filterWithColors = {
              reportTemplateId: filter.reportTemplateId,
              fields: filter.fields.map(field => {
                if (field.fieldId && field.allowedValues && field.allowedValues.length > 0) {
                  // Create field values with their custom assigned colors
                  const fieldValuesWithColors = field.allowedValues.map((allowedValue) => {
                    // Use the custom color from colorAssignments, or use default
                    const assignedColor = field.colorAssignments?.[allowedValue] || defaultColor

                    return {
                      value: allowedValue,
                      color: assignedColor
                    }
                  })

                  return {
                    fieldId: field.fieldId,
                    allowedValues: field.allowedValues, // Keep original structure for compatibility
                    colorAssignments: fieldValuesWithColors // Add color assignments
                  }
                }
                return field
              })
            }
            return filterWithColors
          })

        return {
          defaultMarkerColor: defaultColor,
          reportFieldFilters: filtersWithColors
        }
      },
    },
    async mounted () {
      // Load report templates and knowledge base categories first
      await this.loadReportTemplates()
      await this.loadKnowledgeBaseCategories()

      if (!this.isNew) {
        await this.loadMapData()
      } else {
        this.apiKey = this.generateApiKey()
        this.loading = false
      }
    },
    watch: {
      // Watch for changes in form values and refresh preview
      mapConfig: {
        handler (newConfig, oldConfig) {
          // For immediate visual feedback (dropdowns, sliders, etc.), refresh immediately
          // For text inputs that might be part of mapConfig, we'd need to be more specific
          this.$nextTick(() => {
            this.refreshPreview()
          })
        },
        deep: true
      },
      selectedSpottingAreaId () {
        // Spotting area changes should refresh immediately (dropdown selection)
        this.$nextTick(() => {
          this.refreshPreview()
        })
      },
      mapName () {
        // Map name changes should be debounced (text input)
        this.$nextTick(() => {
          this.debouncedRefreshPreview()
        })
      },
      visibleReportFilters: {
        handler (newFilters, oldFilters) {
          this.$forceUpdate()
          // Debounce preview refresh to avoid "Too many requests" errors
          this.$nextTick(() => {
            this.debouncedRefreshPreview()
          })
        },
        deep: true
      }
    },
    methods: {
      updateMarkerColor: debounce(function (event) {
        this.mapConfig.markerColor = event
      }, 1000),
      updateDefaultMarkerColor (color) {
        this.mapConfig.markerColor = color
      },
      generateAllFieldColors (colors) {
        // Apply the generated colors to all field values
        let colorIndex = 0

        this.visibleReportFilters.forEach(filter => {
          if (filter.reportTemplateId) {
            filter.fields.forEach(field => {
              if (field.fieldId && field.allowedValues && field.allowedValues.length > 0) {
                // Initialize colorAssignments if needed
                if (!field.colorAssignments) {
                  field.colorAssignments = {}
                }

                // Assign colors to each allowed value
                field.allowedValues.forEach(allowedValue => {
                  if (colorIndex < colors.length) {
                    field.colorAssignments[allowedValue] = colors[colorIndex]
                    colorIndex++
                  }
                })
              }
            })
          }
        })

        // Force reactivity update
        this.visibleReportFilters = [...this.visibleReportFilters]
        this.$forceUpdate()
      },
      goBack () {
        this.$router.push('/settings/ecosystem-maps')
      },
      async handleSave () {
        this.saving = true
        // Validate form before saving
        if (!this.validateForm()) {
          console.error('Form validation failed')
          this.saving = false
          return
        }

        try {
          // Get color assignments for report field filters
          const colorData = this.reportFieldFiltersWithColors

          const mapData = {
            name: this.mapName,
            apiKey: this.apiKey,
            zoom: parseInt(this.mapConfig.zoom),
            lat: parseFloat(this.mapConfig.lat),
            lng: parseFloat(this.mapConfig.lng),
            markerIcon: this.mapConfig.markerIcon,
            markerColor: this.mapConfig.markerColor,
            preferredCountry: this.mapConfig.preferredCountry,
            preferredConnectionsCountry: this.mapConfig.preferredConnectionsCountry,
            spottingAreaId: this.selectedSpottingAreaId || null,
            status: this.mapConfig.status,
            showAddOrganisationButton: this.mapConfig.showAddOrganisationButton,
            showContent: this.mapConfig.showContent,
            simplifiedFilterControls: this.mapConfig.simplifiedFilterControls || [],
            allowedDomains: this.allowedDomains.filter(domain => domain.trim() !== ''),
            reportFieldFilters: colorData.reportFieldFilters,
            contentLabel: this.mapConfig.contentLabel,
            showEcosystemLogo: this.mapConfig.showEcosystemLogo
          }

          if (this.isNew) {
            const response = await EcosystemMapsApi.createEcosystemMap(mapData)
            // Direct assignment works in Vue 3 with reactive data
            this.saving = true

            this.map = response.map
            // Redirect to edit mode after creating
            this.$router.push(`/settings/ecosystem-maps/${this.map.id}`).then(() => {
              // Refresh the map in some sort a way
              this.$nextTick(() => {
                this.livePreviewKey++
                window.location.reload()
              })
            })
          } else {
            const response = await EcosystemMapsApi.updateEcosystemMap(this.mapId, mapData)
            // Direct assignment works in Vue 3 with reactive data
            this.map = response
            // Force iframe re-render after save by incrementing key
            this.$nextTick(() => {
              this.livePreviewKey++
            })

            this.saving = false
          }
        } catch (error) {
          console.error('Failed to save map:', error)
          console.error('Failed to save map configuration')
        } finally {
          if (!this.isNew) {
            this.saving = false
          }
        }
      },

      async loadMapData () {
        try {
          this.loading = true
          this.error = null
          const response = await EcosystemMapsApi.fetchEcosystemMap(this.mapId)
          this.map = response
          this.loadMapFromData(this.map)
        } catch (error) {
          console.error('Failed to load map data:', error)
          this.error = 'Failed to load map data'
          console.error('Failed to load map data')
        } finally {
          this.loading = false
        }
      },

      loadMapFromData (map) {
        this.mapName = map.name
        this.apiKey = map.apiKey
        this.mapConfig = {
          zoom: parseInt(map.zoom) || 4,
          lat: parseFloat(map.lat) || 50.0,
          lng: parseFloat(map.lng) || 15.0,
          markerIcon: map.markerIcon || 'default',
          markerColor: map.markerColor || '#3B82F6',
          preferredCountry: map.preferredCountry || null,
          preferredConnectionsCountry: map.preferredConnectionsCountry || null,
          status: map.status || 'draft',
          showAddOrganisationButton: map.showAddOrganisationButton !== undefined ? map.showAddOrganisationButton : true,
          showContent: map.showContent !== undefined ? map.showContent : false,
          simplifiedFilterControls: map.simplifiedFilterControls || [],
          contentLabel: map.contentLabel || 'landscapes',
          showEcosystemLogo: map.showEcosystemLogo || true,
        }

        // Set the theme color for the ColorThemeSelector from the saved default marker color
        this.$nextTick(() => {
          if (this.$refs.colorThemeSelector && map.markerColor) {
            this.$refs.colorThemeSelector.selectedColor = map.markerColor
          }
        })
        this.selectedSpottingAreaId = map.spottingAreaId || ''
        // Load allowed domains or default to one empty field
        this.allowedDomains = map.allowedDomains && map.allowedDomains.length > 0
          ? [...map.allowedDomains]
          : ['']
        // Load report field filters or default to one empty filter
        if (map.reportFieldFilters && map.reportFieldFilters.length > 0) {
          // Deep clone and convert colorAssignments from array to object format
          this.visibleReportFilters = JSON.parse(JSON.stringify(map.reportFieldFilters)).map(filter => ({
            ...filter,
            fields: filter.fields.map(field => {
              // Convert colorAssignments from array [{value, color}] to object {value: color}
              const colorAssignmentsObj = {}
              if (Array.isArray(field.colorAssignments)) {
                field.colorAssignments.forEach(assignment => {
                  colorAssignmentsObj[assignment.value] = assignment.color
                })
              }
              return {
                ...field,
                colorAssignments: colorAssignmentsObj
              }
            })
          }))
        } else {
          this.visibleReportFilters = [{ reportTemplateId: null, fields: [{ fieldId: null, allowedValues: [], colorAssignments: {} }] }]
        }

        // If we have color assignments from the database, we'll need to restore them to the ColorThemeSelector
        // This will happen after the component is mounted and the color selector is available

        // Load field options and template fields for existing filters
        this.visibleReportFilters.forEach(filter => {
          if (filter.reportTemplateId && !this.reportFields[filter.reportTemplateId]) {
            this.loadReportTemplateFields(filter.reportTemplateId)
          }
          filter.fields.forEach(field => {
            if (field.fieldId && !this.reportFieldOptions[field.fieldId]) {
              this.loadFieldOptions(field.fieldId)
            }
          })
        })
      },
      generateApiKey () {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        let result = 'ds_live_'
        for (let i = 0; i < 20; i++) {
          result += chars.charAt(Math.floor(Math.random() * chars.length))
        }
        return result
      },
      copyApiKey () {
        navigator.clipboard.writeText(this.apiKey).then(() => {
          console.log('API key copied to clipboard!')
        }).catch(() => {
          console.error('Failed to copy to clipboard')
        })
      },
      copyEmbedCode () {
        navigator.clipboard.writeText(this.embedCode).then(() => {
          console.log('Embed code copied to clipboard!')
        }).catch(() => {
          console.error('Failed to copy to clipboard')
        })
      },
      copyEmbedCodeScript () {
        navigator.clipboard.writeText(this.embedCodeScript).then(() => {
          console.log('JavaScript embed code copied to clipboard!')
        }).catch(() => {
          console.error('Failed to copy to clipboard')
        })
      },

      validateName () {
        this.touched.name = true
        if (!this.mapName || this.mapName.trim() === '') {
          this.validationErrors.name = 'Map name is required'
        } else if (this.mapName.trim().length < 3) {
          this.validationErrors.name = 'Map name must be at least 3 characters'
        } else if (this.mapName.trim().length > 100) {
          this.validationErrors.name = 'Map name must be less than 100 characters'
        } else {
          delete this.validationErrors.name
        }
      },
      validateZoom () {
        this.touched.zoom = true
        const zoom = parseInt(this.mapConfig.zoom)
        if (isNaN(zoom) || zoom < 1 || zoom > 18) {
          this.validationErrors.zoom = 'Zoom level must be between 1 and 18'
        } else {
          delete this.validationErrors.zoom
        }
      },
      validateLat () {
        this.touched.lat = true
        const lat = parseFloat(this.mapConfig.lat)
        if (isNaN(lat) || lat < -90 || lat > 90) {
          this.validationErrors.lat = 'Latitude must be between -90 and 90'
        } else {
          delete this.validationErrors.lat
        }
      },
      validateLng () {
        this.touched.lng = true
        const lng = parseFloat(this.mapConfig.lng)
        if (isNaN(lng) || lng < -180 || lng > 180) {
          this.validationErrors.lng = 'Longitude must be between -180 and 180'
        } else {
          delete this.validationErrors.lng
        }
      },
      validateSingleDomain (domain) {
        if (!domain || domain.trim() === '') {
          return null // Empty domains are allowed (will be filtered out)
        }

        domain = domain.trim()

        if (domain.includes('://')) {
          return 'Don\'t include http:// or https://'
        } else if (domain.includes(' ')) {
          return 'Domain cannot contain spaces'
        } else if (domain.startsWith('.') || domain.endsWith('.')) {
          return 'Domain cannot start or end with a dot'
        } else if (!domain.includes('.')) {
          return 'Please enter a valid domain (e.g., example.com)'
        } else if (!/^[a-zA-Z0-9.-]+$/.test(domain)) {
          return 'Domain can only contain letters, numbers, dots, and hyphens'
        } else if (domain.length > 253) {
          return 'Domain name is too long'
        } else {
          // Additional validation for common mistakes
          const parts = domain.split('.')
          if (parts.some(part => part.length === 0)) {
            return 'Invalid domain format'
          } else if (parts.some(part => part.startsWith('-') || part.endsWith('-'))) {
            return 'Domain parts cannot start or end with hyphens'
          }
        }

        return null
      },
      validateDomains () {
        this.touched.domains = true
        const validDomains = this.allowedDomains.filter(domain => domain.trim() !== '')

        // Check for duplicates
        const uniqueDomains = new Set(validDomains.map(d => d.trim().toLowerCase()))
        if (uniqueDomains.size !== validDomains.length) {
          this.validationErrors.domains = 'Duplicate domains are not allowed'
          return
        }

        // Validate each domain
        for (const domain of validDomains) {
          const error = this.validateSingleDomain(domain)
          if (error) {
            this.validationErrors.domains = error
            return
          }
        }

        delete this.validationErrors.domains
      },
      getDomainError (index) {
        if (!this.touched.domains || !this.validationErrors.domains) return false
        return this.allowedDomains[index] && this.allowedDomains[index].trim() !== ''
      },
      addDomain () {
        this.allowedDomains.push('')
      },
      removeDomain (index) {
        this.allowedDomains.splice(index, 1)
        this.validateDomains()
      },
      validateForm () {
        this.validateName()
        this.validateZoom()
        this.validateLat()
        this.validateLng()
        this.validateDomains()
        return this.isFormValid
      },

      openMapModal () {
        // Copy current config to temp config
        this.tempMapConfig = {
          zoom: this.mapConfig.zoom,
          lat: this.mapConfig.lat,
          lng: this.mapConfig.lng
        }
        this.showMapModal = true

        // Initialize map after modal is shown
        this.$nextTick(() => {
          this.initializeLeafletMap()
        })
      },

      closeMapModal () {
        this.showMapModal = false
        if (this.leafletMap) {
          this.leafletMap.remove()
          this.leafletMap = null
        }
      },

      applyMapSettings () {
        // Apply temp config to actual config
        this.mapConfig.zoom = this.tempMapConfig.zoom
        this.mapConfig.lat = this.tempMapConfig.lat
        this.mapConfig.lng = this.tempMapConfig.lng
        this.closeMapModal()
      },

      async initializeLeafletMap () {
        try {
          // Dynamically import Leaflet
          const L = await import('leaflet')

          // Import Leaflet CSS
          if (!document.querySelector('link[href*="leaflet"]')) {
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
            document.head.appendChild(link)
          }

          // Fix default marker icon paths
          delete L.Icon.Default.prototype._getIconUrl
          L.Icon.Default.mergeOptions({
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          })

          // Initialize map
          this.leafletMap = L.map('map-selector').setView(
            [this.tempMapConfig.lat, this.tempMapConfig.lng],
            this.tempMapConfig.zoom
          )

          // Add tile layer
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(this.leafletMap)

          // Add center marker
          let centerMarker = L.marker([this.tempMapConfig.lat, this.tempMapConfig.lng])
            .addTo(this.leafletMap)

          // Update config when map moves
          this.leafletMap.on('moveend', () => {
            const center = this.leafletMap.getCenter()
            this.tempMapConfig.lat = center.lat
            this.tempMapConfig.lng = center.lng
            this.tempMapConfig.zoom = this.leafletMap.getZoom()

            // Update marker position
            centerMarker.setLatLng([center.lat, center.lng])
          })

          // Update config when zoom changes
          this.leafletMap.on('zoomend', () => {
            this.tempMapConfig.zoom = this.leafletMap.getZoom()
          })

          // Click to set center
          this.leafletMap.on('click', (e) => {
            this.leafletMap.setView([e.latlng.lat, e.latlng.lng])
            centerMarker.setLatLng([e.latlng.lat, e.latlng.lng])
          })

        } catch (error) {
          console.error('Failed to load Leaflet:', error)
          console.error('Failed to load map. Please check your internet connection.')
        }
      },

      handleIframeError () {
        this.iframeError = true
        // Check if it's a CORS error by trying to access iframe properties
        try {
          if (this.$refs.mapContainer) {
            const iframe = this.$refs.mapContainer.querySelector('iframe')
            if (iframe && iframe.contentWindow === null) {
              this.corsError = true
            }
          }
        } catch (error) {
          this.corsError = true
        }
        console.error('Iframe failed to load, likely due to SSL certificate or CORS issues')
      },

      async tryDirectApi () {
        if (!this.map || !this.map.apiKey) return

        try {
          console.log('Testing direct API access...')
          const response = await EcosystemMapsApi.fetchEcosystemMapEmbedData(this.map.id, this.map.apiKey)
          console.log('Direct API works!', response)
          alert('Direct API access successful! Check console for details.')
        } catch (error) {
          console.error('Direct API failed:', error)
          alert('Direct API also failed. This confirms CORS/SSL issues.')
        }
      },

      getCountryName (countryCode) {
        if (!countryCode) return ''
        const country = this.COUNTRIES.find(c => c.value === countryCode)
        return country ? country.label : countryCode
      },

      getSpottingAreaName (spottingAreaId) {
        if (!spottingAreaId) return ''
        const area = this.availableSpottingAreas.find(a => a.value === spottingAreaId)
        return area ? area.label : `Area ${spottingAreaId}`
      },

      getStatusClass (status) {
        const statusClasses = {
          active: 'text-green-600 font-medium',
          inactive: 'text-gray-500',
          draft: 'text-yellow-600 font-medium'
        }
        return statusClasses[status] || 'text-gray-500'
      },

      handleIframeLoad () {
        this.iframeLoading = false
        this.iframeError = false
      },

      retryEmbed () {
        this.iframeError = false
        this.iframeLoading = true
        this.livePreviewKey++
      },

      refreshPreview () {
        if (this.livePreviewEmbedUrl) {
          this.iframeLoading = true
          this.livePreviewKey++
        }
      },

      debouncedRefreshPreview () {
        // Clear existing timer
        if (this.refreshDebounceTimer) {
          clearTimeout(this.refreshDebounceTimer)
        }

        // Set new timer - wait 1500ms after user stops making changes to avoid rate limiting
        this.refreshDebounceTimer = setTimeout(() => {
          this.refreshPreview()
        }, 1500)
      },

      // Report Filter Management
      addReportFilter () {
        this.visibleReportFilters.push({
          reportTemplateId: null,
          fields: [{ fieldId: null, allowedValues: [], colorAssignments: {} }]
        })
      },
      removeReportFilter (index) {
        this.visibleReportFilters.splice(index, 1)
      },
      clearReportTemplate (index) {
        this.visibleReportFilters[index].reportTemplateId = null
        this.visibleReportFilters[index].fields = []

        // Force reactivity update by creating a new array reference
        this.visibleReportFilters = [...this.visibleReportFilters]
      },
      onReportTemplateChange (index, templateId) {
        this.visibleReportFilters[index].reportTemplateId = templateId
        this.visibleReportFilters[index].fields = [{ fieldId: null, allowedValues: [], colorAssignments: {} }]

        // Force reactivity update by creating a new array reference
        this.visibleReportFilters = [...this.visibleReportFilters]

        // Load fields for this template if not already loaded
        if (templateId && !this.reportFields[templateId]) {
          this.loadReportTemplateFields(templateId)
        }
      },
      addFieldToReport (reportIndex) {
        this.visibleReportFilters[reportIndex].fields.push({ fieldId: null, allowedValues: [], colorAssignments: {} })
      },
      removeField (reportIndex, fieldIndex) {
        this.visibleReportFilters[reportIndex].fields.splice(fieldIndex, 1)
      },
      onFieldChange (reportIndex, fieldIndex, fieldId) {
        // Update field ID and reset allowed values
        this.visibleReportFilters[reportIndex].fields[fieldIndex].fieldId = fieldId
        this.visibleReportFilters[reportIndex].fields[fieldIndex].allowedValues = []
        this.visibleReportFilters[reportIndex].fields[fieldIndex].colorAssignments = {}

        // Force reactivity update by creating a new array reference
        this.visibleReportFilters = [...this.visibleReportFilters]

        // Always load field options when field changes
        if (fieldId) {
          this.loadFieldOptions(fieldId)
        }
      },
      onFieldValueToggle (reportIndex, fieldIndex, value) {
        const fieldConfig = this.visibleReportFilters[reportIndex].fields[fieldIndex]

        // Initialize colorAssignments if not exists
        if (!fieldConfig.colorAssignments) {
          fieldConfig.colorAssignments = {}
        }

        // If the value was just checked (added to allowedValues), assign a default color
        if (fieldConfig.allowedValues.includes(value) && !fieldConfig.colorAssignments[value]) {
          fieldConfig.colorAssignments[value] = this.generateDistinctColor(reportIndex, fieldIndex)
        }

        // Force reactivity update
        this.$forceUpdate()
      },
      onFieldValueColorChange (reportIndex, fieldIndex, value) {
        // Color is already bound via v-model
        // The visibleReportFilters watcher will handle debounced preview refresh
      },
      generateDistinctColor (reportIndex, fieldIndex) {
        // Generate a distinct color based on how many colors are already assigned
        const allColors = []
        this.visibleReportFilters.forEach(filter => {
          filter.fields.forEach(field => {
            if (field.colorAssignments) {
              allColors.push(...Object.values(field.colorAssignments))
            }
          })
        })

        // Use a simple hue rotation to generate distinct colors
        const hueStep = 360 / Math.max(12, allColors.length + 1) // At least 12 distinct hues
        const hue = (allColors.length * hueStep) % 360

        // Convert HSL to hex
        return this.hslToHex(hue, 70, 50)
      },
      hslToHex (h, s, l) {
        s /= 100
        l /= 100
        const c = (1 - Math.abs(2 * l - 1)) * s
        const x = c * (1 - Math.abs((h / 60) % 2 - 1))
        const m = l - c / 2
        let r = 0, g = 0, b = 0

        if (0 <= h && h < 60) {
          r = c;
          g = x;
          b = 0
        } else if (60 <= h && h < 120) {
          r = x;
          g = c;
          b = 0
        } else if (120 <= h && h < 180) {
          r = 0;
          g = c;
          b = x
        } else if (180 <= h && h < 240) {
          r = 0;
          g = x;
          b = c
        } else if (240 <= h && h < 300) {
          r = x;
          g = 0;
          b = c
        } else if (300 <= h && h < 360) {
          r = c;
          g = 0;
          b = x
        }

        r = Math.round((r + m) * 255)
        g = Math.round((g + m) * 255)
        b = Math.round((b + m) * 255)

        return '#' + [r, g, b].map(x => {
          const hex = x.toString(16)
          return hex.length === 1 ? '0' + hex : hex
        }).join('')
      },
      getAvailableFieldsForTemplate (templateId) {
        return this.reportFields[templateId] || []
      },
      getAvailableFieldsForTemplateExcludingSelected (templateId, reportIndex, currentFieldIndex) {
        const allFields = this.reportFields[templateId] || []

        // Get already selected field IDs for this report template
        const selectedFieldIds = this.visibleReportFilters[reportIndex]?.fields
          ?.map((field, index) => index !== currentFieldIndex ? field.fieldId : null)
          ?.filter(fieldId => fieldId !== null) || []

        // Filter out already selected fields
        return allFields.filter(field => !selectedFieldIds.includes(field.id))
      },
      getFieldLabel (fieldId) {
        // Find field label across all templates
        for (const templateId in this.reportFields) {
          const field = this.reportFields[templateId].find(f => f.id === fieldId)
          if (field) return field.label
        }
        return `Field ${fieldId}`
      },
      getFieldOptions (fieldId) {
        return this.reportFieldOptions[fieldId] || []
      },
      toggleAllFieldValues (reportIndex, fieldIndex) {
        const fieldConfig = this.visibleReportFilters[reportIndex].fields[fieldIndex]
        const allOptions = this.getFieldOptions(fieldConfig.fieldId)

        if (fieldConfig.allowedValues.length === allOptions.length) {
          // Deselect all
          fieldConfig.allowedValues = []
        } else {
          // Select all
          fieldConfig.allowedValues = allOptions.map(option => option.value)
        }
      },
      async loadReportTemplates () {
        try {
          const reports = await EcosystemReports.get()
          this.reportTemplates = reports || []

          // Pre-load report fields for each template
          this.reportFields = {}
          reports.forEach(report => {
            if (report.reportFields) {
              this.reportFields[report.id] = report.reportFields.filter(field =>
                field.can_be_used_as_filter &&
                ['options', 'multi_select', 'checkbox'].includes(field.type)
              )
            }
          })
        } catch (error) {
          console.error('Failed to load report templates:', error)
          this.reportTemplates = []
        }
      },
      async loadKnowledgeBaseCategories () {
        try {
          const response = await fetch('/api/knowledge-base/categories')
          if (!response.ok) {
            throw new Error('Failed to fetch knowledge base categories')
          }
          const data = await response.json()
          this.knowledgeBaseCategories = data.data || []
        } catch (error) {
          console.error('Failed to load knowledge base categories:', error)
          this.knowledgeBaseCategories = []
        }
      },
      async loadReportTemplateFields (templateId) {
        // Fields are already loaded in loadReportTemplates, so just auto-populate first field if needed
        const reportFilter = this.visibleReportFilters.find(f => f.reportTemplateId === templateId)
        if (reportFilter && reportFilter.fields.length === 1 && !reportFilter.fields[0].fieldId) {
          if (this.reportFields[templateId] && this.reportFields[templateId].length > 0) {
            reportFilter.fields[0].fieldId = this.reportFields[templateId][0].id
            reportFilter.fields[0].colorAssignments = reportFilter.fields[0].colorAssignments || {}
            this.loadFieldOptions(this.reportFields[templateId][0].id)
          }
        }
      },
      async loadFieldOptions (fieldId) {
        try {
          // Find the field in our loaded data
          let field = null
          Object.values(this.reportFields).forEach(fields => {
            const foundField = fields.find(f => f.id === fieldId)
            if (foundField) {
              field = foundField
            }
          })

          if (field) {
            // Convert field options to the format expected by our dropdowns
            let options = []

            if (field.options_i18n && typeof field.options_i18n === 'object') {
              // Use translated options if available
              const currentLocale = this.$i18n?.locale || 'en'

              // Try current locale, then en, then first available locale
              const translatedOptions = field.options_i18n[currentLocale] ||
                field.options_i18n['en'] ||
                field.options_i18n[Object.keys(field.options_i18n)[0]] ||
                {}

              if (Object.keys(translatedOptions).length > 0) {
                options = Object.keys(translatedOptions).map(key => ({
                  value: key,
                  label: translatedOptions[key]
                }))
              } else if (Array.isArray(field.options) && field.options.length > 0) {
                // Fallback to original options array
                options = field.options.map(option => ({
                  value: option,
                  label: option
                }))
              }
            } else if (Array.isArray(field.options) && field.options.length > 0) {
              // Use original options as both value and label
              options = field.options.map(option => ({
                value: option,
                label: option
              }))
            }

            this.reportFieldOptions[fieldId] = options
          } else {
            this.reportFieldOptions[fieldId] = []
          }
        } catch (error) {
          console.error('Failed to load field options:', error)
          this.reportFieldOptions[fieldId] = []
        }
      },
    },
    beforeUnmount () {
      // Clean up debounce timer when component is destroyed
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer)
        this.refreshDebounceTimer = null
      }
    }
  })
</script>

<style lang="scss" scoped>
  .tw-container {
    max-width: 1200px;
  }

  code {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    line-height: 1.4;
  }
</style>
